import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import moment from 'moment';

import { BaseComponent } from '../../components/base/base.component';
import { SessionService } from '../../_services/session/session.service';
import { SalesFilterService, SalesFilters } from './sales-filter.service';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DialogService } from 'primeng/dynamicdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';

// Components
import { FormSaleComponent } from '../../components/forms/form-sale/form-sale.component';

@Component({
  standalone: true,
  selector: 'app-sales',
  templateUrl: './sales.component.html',
  styleUrls: ['./sales.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    TooltipModule,
    DatePickerModule,
    InputTextModule,
  ],
  providers: [DialogService, SalesFilterService],
})
export class SalesComponent extends BaseComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  // Filters
  public searchText: string = '';
  public dateRange: Date[] | null = null;
  public activeQuickFilter: 'today' | 'week' | 'month' | 'all' | null = 'month';

  // Mobile state
  public showMobileFilters: boolean = false;

  constructor(
    public session: SessionService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private dialogService: DialogService,
    public filterService: SalesFilterService,
  ) {
    super(session);
  }

  ngOnInit(): void {
    super.ngOnInit();
    // Initialize with default month filter
    this.setDateFilter('month');

    // Subscribe to filter changes to keep local state in sync
    this.subscriptions.push(
      this.filterService.filters$.subscribe((filters) => {
        this.searchText = filters.searchText;
        this.dateRange = filters.dateRange;
        this.activeQuickFilter = filters.activeQuickFilter;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  // Filter methods
  onSearchChange(): void {
    this.filterService.setSearchText(this.searchText);
  }

  onDateRangeChange(): void {
    this.activeQuickFilter = null;
    this.filterService.setDateRange(this.dateRange);
  }

  setDateFilter(preset: 'today' | 'week' | 'month' | 'all'): void {
    this.activeQuickFilter = preset;

    switch (preset) {
      case 'today':
        const today = moment();
        this.dateRange = [
          today.clone().startOf('day').toDate(),
          today.clone().endOf('day').toDate(),
        ];
        break;
      case 'week':
        this.dateRange = [
          moment().startOf('isoWeek').toDate(),
          moment().endOf('isoWeek').toDate(),
        ];
        break;
      case 'month':
        this.dateRange = [
          moment().startOf('month').toDate(),
          moment().endOf('month').toDate(),
        ];
        break;
      case 'all':
        this.dateRange = null;
        break;
    }

    this.filterService.updateFilters({
      dateRange: this.dateRange,
      activeQuickFilter: preset,
    });
  }

  clearFilters(): void {
    this.searchText = '';
    this.dateRange = null;
    this.activeQuickFilter = 'all';
    this.filterService.clearFilters();
  }

  get hasActiveFilters(): boolean {
    return this.filterService.hasActiveFilters();
  }

  get canCreateSales(): boolean {
    return this.canAccessModule('sales', {
      anyPermission: ['SALES.CREATE'],
    });
  }

  toggleMobileFilters(): void {
    this.showMobileFilters = !this.showMobileFilters;
  }

  openCreateSale(): void {
    if (!this.canCreateSales) return;
    const ref = this.dialogService.open(FormSaleComponent, {
      closable: true,
      modal: true,
      header: this.translate.instant('SALES.TITLE.ADD'),
      width: '52rem',
      styleClass: 'vc-dialog-lg vc-dialog--sale',
      inputValues: {
        onCreate: () => {
          ref.close();
          // Emit event to refresh list
          window.dispatchEvent(new CustomEvent('sales:refresh'));
        },
      },
    });
  }
}
